import { CHAIN_ID } from '@derivation-tech/context';
import _ from 'lodash';
import { useMemo } from 'react';

import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { WrappedPosition } from '@/entities/WrappedPosition';
import { useAppSelector } from '@/hooks';
import { useDappChainConfig, useUserAddr } from '@/hooks/web3/useChain';
import { IGateAccountState, IGateBalanceInfo } from '@/types/balance';
import { isNativeTokenAddr } from '@/utils/token';

import { utils } from '@synfutures/sdks-perp';
import { useNativeToken } from '../chain/hook';
import { selectGateAccountState } from './balanceSlice';
import { useFetchUserGateValue, useFetchUserTokenBalanceList } from './query';
import { ITokenBalanceFromApiResponse, IUserTokenBalance } from './types';

export function useTokenBalanceList(chainId: CHAIN_ID | undefined): ITokenBalanceFromApiResponse {
  // const allTokenBalanceInfoMapState = useTokenBalanceMapState(chainId);
  const userAddr = useUserAddr();
  const { data } = useFetchUserTokenBalanceList(chainId, userAddr);
  const nativeToken = useNativeToken(chainId);
  const tokenBalanceList = useMemo(() => {
    if (!nativeToken) return [];
    const coinBalanceList = _.orderBy(
      data,
      [
        (coin): number => {
          if (coin.address === nativeToken.address) {
            return -1;
          }
          return coin.symbol?.toLowerCase().charCodeAt(0);
        },
      ],
      ['asc'],
    ).map((coin) => {
      return coin;
    });

    return coinBalanceList;
  }, [data, nativeToken]);
  return tokenBalanceList;
}

export function useTokenBalanceInfo(
  tokenAddress: string | undefined,
  chainId: CHAIN_ID | undefined,
): IUserTokenBalance | undefined {
  const tokenBalanceList = useTokenBalanceList(chainId);
  return useMemo(() => {
    if (!tokenAddress || !tokenBalanceList) return;
    return tokenBalanceList.find((item) => item.address === tokenAddress);
  }, [tokenAddress, tokenBalanceList]);
}

/**
 * get token balance(return limit balance of native token)
 * @param tokenAddress token address
 * @param chainId
 * @param needLimitNativeTokenBalance limit the balance of native token for send tx gas,default true
 * @returns balance of token
 */
export function useTokenBalance(
  tokenAddress: string | undefined,
  chainId: CHAIN_ID | undefined,
  needLimitNativeTokenBalance = false,
): WrappedBigNumber {
  const userAddr = useUserAddr();
  const { data: tokenBalances } = useFetchUserTokenBalanceList(chainId, userAddr);
  const dappConfig = useDappChainConfig(chainId);
  const tokenBalance = useMemo(
    () => tokenBalances?.find((t) => t.address.toLowerCase() === tokenAddress?.toLowerCase()),
    [tokenAddress, tokenBalances],
  );
  return useMemo(() => {
    //  limit the balance of native token
    if (
      dappConfig?.minNativeTokenKeep &&
      needLimitNativeTokenBalance &&
      tokenBalance?.balance &&
      isNativeTokenAddr(tokenBalance?.address)
    ) {
      const nativeTokenMinGasPriceLimit = dappConfig?.minNativeTokenKeep;
      if (nativeTokenMinGasPriceLimit) {
        return tokenBalance?.balance.min(nativeTokenMinGasPriceLimit).keepPositive();
      }
    }
    return tokenBalance?.balance || WrappedBigNumber.ZERO;
  }, [
    dappConfig?.minNativeTokenKeep,
    needLimitNativeTokenBalance,
    tokenBalance?.address,
    tokenBalance?.balance?.stringValue,
  ]);
}

export function useTokenGateBalanceByChainIdAndAddress(
  chainId: CHAIN_ID | undefined,
  tokenAddress: string | undefined,
): IGateBalanceInfo | undefined {
  const account = useUserAddr();
  // const tokenBalance = useAppSelector(selectTokenGateBalanceByChainIdAndAddress(chainId, account, tokenAddress));
  // return useMemo(() => bigNumberObjectCheck(tokenBalance), [tokenBalance]);
  const gateBalances = useUserGateBalanceList(chainId, account);

  return useMemo(() => {
    return gateBalances.find((b) => b.address === tokenAddress);
  }, [gateBalances, tokenAddress]);
}

// export function useTokenGateBalanceMap(
//   chainId: CHAIN_ID | undefined,
//   userAddr: string | undefined,
// ): ITokenBalanceInfoMap {
//   const tokenBalanceMap = useAppSelector(selectTokenGateBalanceByChainId(chainId, userAddr));
//   return useMemo(() => bigNumberObjectCheck(tokenBalanceMap), [tokenBalanceMap]);
// }

export function useGateAccountState(chainId: CHAIN_ID | undefined): IGateAccountState {
  const chainTradeAccountState = useAppSelector(selectGateAccountState);

  return useMemo(() => {
    if (chainId && chainTradeAccountState[chainId]) return chainTradeAccountState[chainId];
    return {} as IGateAccountState;
  }, [chainId, chainTradeAccountState]);
}

/**
 * get all balance of token and vault balance
 * @param tokenAddress
 * @param chainId
 * @param plusGateBalance
 * @param needLimitNativeTokenBalance
 * @returns
 */
export function useAvailableTokenBalance(
  tokenAddress: string | undefined,
  chainId: CHAIN_ID | undefined,
  plusGateBalance = true,
  needLimitNativeTokenBalance = true,
): WrappedBigNumber {
  const tokenBalance = useTokenBalance(tokenAddress, chainId, needLimitNativeTokenBalance);
  const gateBalance = useTokenGateBalanceByChainIdAndAddress(chainId, tokenAddress);
  return useMemo(() => {
    let balance = tokenBalance;
    if (plusGateBalance && tokenBalance) {
      balance = tokenBalance.add(gateBalance?.balance || WrappedBigNumber.ZERO);
    }
    return balance || WrappedBigNumber.ZERO;
  }, [plusGateBalance, tokenBalance, gateBalance?.balance?.wadValue?._hex]);
}

export function useDisplayBalance(
  position: WrappedPosition | undefined,
  transferIn: boolean,
  chainId: CHAIN_ID | undefined,
  tokenAddress: string | undefined,
): WrappedBigNumber {
  const tokenBalance = useAvailableTokenBalance(tokenAddress || '', chainId);
  const availablePositionMargin = useMemo(() => {
    if (!transferIn && position?.size) {
      const margin = utils.positionMaxWithdrawableMargin(
        position,
        position.rootPair,
        position.rootInstrument.initialMarginRatio,
      );
      if (margin) {
        return WrappedBigNumber.from(margin);
      }
    }
    return WrappedBigNumber.ZERO;
  }, [transferIn, position?.size, position?.unrealizedPnl, position?.balance]);
  const displayBalance = useMemo(() => {
    if (!transferIn) {
      return availablePositionMargin;
    }
    return tokenBalance;
  }, [tokenBalance?.wadValue?._hex, availablePositionMargin, transferIn]);
  return displayBalance;
}

export function useUserGateBalanceList(chainId: number | undefined, userAddr: string | undefined): IGateBalanceInfo[] {
  const { data: gateValueResponse } = useFetchUserGateValue(chainId, userAddr);
  // const tokens = useQuoteTokens(chainId);
  const portfolios = useMemo(() => gateValueResponse?.portfolios || [], [gateValueResponse]);
  const userGateBalanceList = useMemo(() => {
    if (portfolios) {
      return portfolios.map((token) => {
        // // const balance = portfolios.find((b) => b.address === token.address);
        // // if (!balance)
        // //   return {
        // //     ...token,
        // //     balance: WrappedBigNumber.from('0'),
        // //     decimals: Number(token.decimals),
        // //     address: token.address,
        // //   };
        // const info: IGateBalanceInfo = {
        //   ...token,
        //   balance: WrappedBigNumber.from(token?.balance || '0'),
        //   decimals: Number(token.decimals),
        //   address: token.address,
        // };
        return token;
      });
    }
    return [];
  }, [portfolios]);
  return userGateBalanceList;
}

export function useUserIsFetchedGateBalanceList(chainId: number | undefined, userAddr: string | undefined): boolean {
  const { isFetched } = useFetchUserGateValue(chainId, userAddr);
  return isFetched;
}
